/**
 * Kullanıcılar ekranı.
 *
 * Rol atamanın yanında **kişi bazlı ince ayar** var: role ek yetki verilebilir
 * ya da rolde olsa bile geri alınabilir. Geri alma daha güçlüdür — "yönetici
 * ama silemez" demek mümkün olsun.
 *
 * Tehlikeli işlemler (rol değiştirme, hesap kapatma, şifre sıfırlama, silme)
 * **daima onay penceresiyle** yapılır ve pencere ne olacağını açıkça yazar.
 * "Emin misiniz?" tek başına yeterli değildir.
 *
 * Üretilen şifre **bir kez** gösterilir: veritabanında yalnızca scrypt özeti
 * var, geri getirilemez. Bu yüzden köşede kaybolan bildirim değil, kapatılması
 * gereken bir pencere kullanılır.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  KeyRound,
  Lock,
  ShieldCheck,
  Trash2,
  UserPlus,
  UserRoundCheck,
  UserRoundX,
} from 'lucide-react';
import { api, type PanelKullanicisi, type Rol } from '../lib/api';
import { Kabuk } from '../components/Kabuk';
import { Kart, KartBasligi, KartGovdesi } from '../components/ui/Kart';
import { Dugme } from '../components/ui/Dugme';
import { Avatar } from '../components/ui/Avatar';
import { Girdi, Secim } from '../components/ui/Girdi';
import { BosDurum, HataKutusu, Yukleniyor } from '../components/Durumlar';
import { basariBildir, hataBildir, onayIste, sifreGoster } from '../lib/bildirim';
import { goreliZaman } from '../lib/format';
import { cn } from '../lib/cn';

/** Rol etiketi tonu — sistem rolü mü özel rol mü. */
function rolTonu(systemKey: string | null): string {
  if (systemKey === 'SUPER_ADMIN') return 'bg-[var(--state-bad-soft)] text-[var(--state-bad)]';
  if (systemKey === 'ADMIN') return 'bg-[var(--state-info-soft)] text-[var(--state-info)]';
  if (systemKey) return 'bg-[var(--state-ok-soft)] text-[var(--state-ok)]';
  return 'bg-[var(--state-done-soft)] text-[var(--state-done)]';
}

// ---------------------------------------------------------------------------

interface SatirProps {
  kullanici: PanelKullanicisi;
  roller: Rol[];
  duzenlenebilir: boolean;
  silinebilir: boolean;
  islemde: boolean;
  onRolDegistir: (rolId: string) => void;
  onDurumDegistir: (aktif: boolean) => void;
  onSifreSifirla: () => void;
  onSil: () => void;
}

function KullaniciSatiri({
  kullanici,
  roller,
  duzenlenebilir,
  silinebilir,
  islemde,
  onRolDegistir,
  onDurumDegistir,
  onSifreSifirla,
  onSil,
}: SatirProps) {
  const [ayrintiAcik, setAyrintiAcik] = useState(false);

  return (
    <li
      className={cn(
        'kap flex flex-col gap-3 rounded-[var(--radius-md)] border p-3',
        kullanici.isActive ? 'border-[var(--line)]' : 'border-[var(--line)] opacity-60',
        // Kendi satırın işaretli: yanlış hesapta işlem yapma.
        kullanici.isSelf && 'border-[var(--accent)]'
      )}
    >
      <div className="flex flex-col gap-3 @2xl:flex-row @2xl:items-start @2xl:justify-between">
        <div className="flex min-w-0 gap-3">
          {/* Baş harf avatarı: satırı taranabilir kılar — göz "MK"yi isimden
              daha hızlı ayırır. Renk isimden türer, her ekranda aynı. */}
          <Avatar ad={kullanici.fullName} anahtar={kullanici.email} boy="md" />
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="truncate text-sm font-medium text-[var(--ink)]">{kullanici.fullName}</span>
              {kullanici.isSelf && (
                <span className="rounded-full bg-[var(--accent-soft)] px-2 py-0.5 text-xs font-medium text-[var(--accent)]">
                  siz
                </span>
              )}
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 text-xs font-semibold',
                  rolTonu(kullanici.roleSystemKey)
                )}
              >
                {kullanici.roleName}
              </span>
              {!kullanici.isActive && (
                <span className="rounded-full bg-[var(--state-done-soft)] px-2 py-0.5 text-xs font-semibold text-[var(--state-done)]">
                  kapalı
                </span>
              )}
              {kullanici.locked && (
                <span className="inline-flex items-center gap-1 rounded-full bg-[var(--state-bad-soft)] px-2 py-0.5 text-xs font-semibold text-[var(--state-bad)]">
                  <Lock size={11} aria-hidden="true" />
                  kilitli
                </span>
              )}
            </div>
            <span className="truncate text-xs text-[var(--ink-muted)]">{kullanici.email}</span>
            <span className="text-xs text-[var(--ink-faint)]">
              {kullanici.lastLoginAt
                ? `Son giriş ${goreliZaman(kullanici.lastLoginAt)}`
                : 'Henüz hiç giriş yapmadı'}
              {kullanici.failedAttempts > 0 && (
                <span className="text-[var(--state-bad)]">
                  {' · '}
                  <span className="rakam">{kullanici.failedAttempts}</span> hatalı deneme
                </span>
              )}
              {' · '}
              <span className="rakam">{kullanici.effectivePermissions.length}</span> yetki
            </span>
          </div>
        </div>

        {duzenlenebilir && !kullanici.isSelf && (
          <div className="@2xl:w-56">
            <Secim
              etiket="Rol"
              value={kullanici.roleId}
              disabled={islemde}
              onChange={(olay) => onRolDegistir(olay.target.value)}
            >
              {roller.map((rol) => (
                <option key={rol.id} value={rol.id}>
                  {rol.name}
                </option>
              ))}
            </Secim>
          </div>
        )}
      </div>

      {/* Kişi bazlı sapmalar — varsa görünür olmalı, yoksa yer kaplamamalı. */}
      {(kullanici.extraPermissions.length > 0 || kullanici.deniedPermissions.length > 0) && (
        <div className="flex flex-wrap gap-1.5">
          {kullanici.extraPermissions.map((izin) => (
            <span
              key={`e-${izin}`}
              className="rakam rounded-[var(--radius-sm)] bg-[var(--state-ok-soft)] px-1.5 py-0.5 text-xs text-[var(--state-ok)]"
              title="Role ek olarak verilmiş"
            >
              + {izin}
            </span>
          ))}
          {kullanici.deniedPermissions.map((izin) => (
            <span
              key={`d-${izin}`}
              className="rakam rounded-[var(--radius-sm)] bg-[var(--state-bad-soft)] px-1.5 py-0.5 text-xs text-[var(--state-bad)]"
              title="Rolde olsa bile geri alınmış"
            >
              − {izin}
            </span>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 border-t border-[var(--line)] pt-3">
        {duzenlenebilir && (
          <>
            <Dugme boy="sm" ikon={<KeyRound size={13} />} disabled={islemde} onClick={onSifreSifirla}>
              Şifre sıfırla
            </Dugme>
            {kullanici.isActive ? (
              <Dugme
                boy="sm"
                gorunum="tehlike"
                ikon={<UserRoundX size={13} />}
                disabled={islemde || kullanici.isSelf}
                title={kullanici.isSelf ? 'Kendi hesabınızı kapatamazsınız' : undefined}
                onClick={() => onDurumDegistir(false)}
              >
                Hesabı kapat
              </Dugme>
            ) : (
              <Dugme
                boy="sm"
                ikon={<UserRoundCheck size={13} />}
                disabled={islemde}
                onClick={() => onDurumDegistir(true)}
              >
                Hesabı aç
              </Dugme>
            )}
          </>
        )}
        {silinebilir && !kullanici.isSelf && (
          <Dugme boy="sm" gorunum="tehlike" ikon={<Trash2 size={13} />} disabled={islemde} onClick={onSil}>
            Sil
          </Dugme>
        )}

        <Dugme
          boy="sm"
          gorunum="sessiz"
          ikon={<ChevronDown size={13} className={cn(ayrintiAcik && 'rotate-180')} />}
          aria-expanded={ayrintiAcik}
          onClick={() => setAyrintiAcik((a) => !a)}
        >
          Yetkileri gör
        </Dugme>
      </div>

      {ayrintiAcik && (
        <div className="flex flex-wrap gap-1.5 border-t border-[var(--line)] pt-3">
          {kullanici.isSuperAdmin ? (
            <p className="text-xs text-[var(--ink-muted)]">
              Süper Yönetici — tüm yetkilere sahiptir, kısıtlanamaz.
            </p>
          ) : kullanici.effectivePermissions.length === 0 ? (
            <p className="text-xs text-[var(--ink-muted)]">Hiçbir yetkisi yok.</p>
          ) : (
            kullanici.effectivePermissions.map((izin) => (
              <span
                key={izin}
                className="rakam rounded-[var(--radius-sm)] bg-[var(--surface-sunken)] px-1.5 py-0.5 text-xs text-[var(--ink-muted)]"
              >
                {izin}
              </span>
            ))
          )}
        </div>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------

function EklemeFormu({
  roller,
  onEkle,
  bekliyor,
}: {
  roller: Rol[];
  onEkle: (govde: { email: string; fullName: string; roleId: string }) => void;
  bekliyor: boolean;
}) {
  const [email, setEmail] = useState('');
  const [adSoyad, setAdSoyad] = useState('');
  // Varsayılan: en dar yetkili rol. Yanlışlıkla geniş yetki verilmesin.
  const [rolId, setRolId] = useState(roller.find((r) => r.systemKey === 'VIEWER')?.id ?? roller[0]?.id ?? '');

  const secili = roller.find((r) => r.id === rolId);

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(olay) => {
        olay.preventDefault();
        onEkle({ email: email.trim(), fullName: adSoyad.trim(), roleId: rolId });
        setEmail('');
        setAdSoyad('');
      }}
    >
      <div className="grid grid-cols-1 gap-3 @2xl:grid-cols-3">
        <Girdi
          etiket="Ad soyad"
          value={adSoyad}
          onChange={(o) => setAdSoyad(o.target.value)}
          required
          minLength={2}
          placeholder="Ozan Yıldırım"
          autoComplete="off"
        />
        <Girdi
          etiket="E-posta"
          type="email"
          value={email}
          onChange={(o) => setEmail(o.target.value)}
          required
          placeholder="ozan@deercase.com"
          autoComplete="off"
        />
        <Secim etiket="Rol" value={rolId} onChange={(o) => setRolId(o.target.value)}>
          {roller.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </Secim>
      </div>

      {/* Rolün ne yapabildiği seçim anında yazılır — sonradan şaşırma olmasın. */}
      {secili && (
        <p className="rounded-[var(--radius-sm)] bg-[var(--surface-sunken)] px-3 py-2 text-xs text-[var(--ink-muted)]">
          <span className="font-medium text-[var(--ink)]">{secili.name}:</span>{' '}
          {secili.description ?? 'Açıklama yok.'}{' '}
          <span className="rakam">({secili.permissions.length} yetki)</span>
        </p>
      )}

      <div className="flex">
        <Dugme
          type="submit"
          gorunum="birincil"
          ikon={<UserPlus size={15} />}
          bekliyor={bekliyor}
          disabled={!rolId}
        >
          Kullanıcı ekle
        </Dugme>
      </div>

      <p className="text-xs text-[var(--ink-faint)]">
        Şifreyi sistem üretir ve bir kez gösterir. Kullanıcıya güvenli bir kanalla iletin — WhatsApp/e-posta
        değil, yüz yüze ya da şifre yöneticisi ile.
      </p>
    </form>
  );
}

// ---------------------------------------------------------------------------

export function Kullanicilar() {
  const sorguIstemcisi = useQueryClient();
  const [formAcik, setFormAcik] = useState(false);
  const [islemdekiId, setIslemdekiId] = useState<string | null>(null);

  const kullanicilar = useQuery({ queryKey: ['kullanicilar'], queryFn: api.kullanicilar });
  const roller = useQuery({ queryKey: ['roller'], queryFn: api.roller });

  const tazele = () => {
    void sorguIstemcisi.invalidateQueries({ queryKey: ['kullanicilar'] });
    void sorguIstemcisi.invalidateQueries({ queryKey: ['roller'] });
  };

  const ekle = useMutation({
    mutationFn: api.kullaniciEkle,
    onSuccess: async (sonuc) => {
      tazele();
      setFormAcik(false);
      if (sonuc.generatedPassword) {
        await sifreGoster(sonuc.email, sonuc.generatedPassword, sonuc.message);
      } else {
        basariBildir(sonuc.message, 'Kullanıcı eklendi');
      }
    },
    onError: hataBildir,
  });

  const guncelle = useMutation({
    mutationFn: ({ id, yama }: { id: string; yama: Parameters<typeof api.kullaniciGuncelle>[1] }) =>
      api.kullaniciGuncelle(id, yama),
    onSuccess: (sonuc) => {
      basariBildir(sonuc.message);
      tazele();
    },
    onError: hataBildir,
    onSettled: () => setIslemdekiId(null),
  });

  const sifirla = useMutation({
    mutationFn: api.sifreSifirla,
    onSuccess: async (sonuc, id) => {
      const kullanici = kullanicilar.data?.items.find((k) => k.id === id);
      await sifreGoster(kullanici?.email ?? 'Kullanıcı', sonuc.generatedPassword, sonuc.message);
      tazele();
    },
    onError: hataBildir,
    onSettled: () => setIslemdekiId(null),
  });

  const sil = useMutation({
    mutationFn: api.kullaniciSil,
    onSuccess: (sonuc) => {
      basariBildir(sonuc.message);
      tazele();
    },
    onError: hataBildir,
    onSettled: () => setIslemdekiId(null),
  });

  const rolDegistir = async (kullanici: PanelKullanicisi, yeniRolId: string) => {
    if (yeniRolId === kullanici.roleId) return;
    const yeniRol = roller.data?.items.find((r) => r.id === yeniRolId);

    const onay = await onayIste({
      baslik: 'Rol değiştirilsin mi?',
      mesaj:
        `${kullanici.fullName} artık "${yeniRol?.name ?? 'yeni rol'}" olacak ` +
        `(${yeniRol?.permissions.length ?? 0} yetki). ` +
        'Açık oturumu kapatılacak; yeni yetki bir sonraki girişte geçerli olur.',
      onayMetni: 'Rolü değiştir',
      tehlikeli: yeniRol?.systemKey === 'SUPER_ADMIN',
    });
    if (!onay) {
      tazele(); // seçim kutusunu eski değerine döndür
      return;
    }
    setIslemdekiId(kullanici.id);
    guncelle.mutate({ id: kullanici.id, yama: { roleId: yeniRolId } });
  };

  const durumDegistir = async (kullanici: PanelKullanicisi, aktif: boolean) => {
    const onay = await onayIste({
      baslik: aktif ? 'Hesap açılsın mı?' : 'Hesap kapatılsın mı?',
      mesaj: aktif
        ? `${kullanici.fullName} tekrar giriş yapabilecek ve hatalı deneme sayacı sıfırlanacak.`
        : `${kullanici.fullName} panele giremeyecek ve açık oturumu hemen kapatılacak. Kayıtları silinmez.`,
      onayMetni: aktif ? 'Hesabı aç' : 'Hesabı kapat',
      tehlikeli: !aktif,
    });
    if (!onay) return;
    setIslemdekiId(kullanici.id);
    guncelle.mutate({ id: kullanici.id, yama: { isActive: aktif } });
  };

  const sifreSifirla = async (kullanici: PanelKullanicisi) => {
    const onay = await onayIste({
      baslik: 'Şifre sıfırlansın mı?',
      mesaj:
        `${kullanici.fullName} için yeni bir şifre üretilecek ve eski şifre çalışmayacak. ` +
        'Açık oturumları kapatılır. Yeni şifre size bir kez gösterilecek.',
      onayMetni: 'Şifreyi sıfırla',
      tehlikeli: true,
    });
    if (!onay) return;
    setIslemdekiId(kullanici.id);
    sifirla.mutate(kullanici.id);
  };

  const silmeyiSor = async (kullanici: PanelKullanicisi) => {
    const onay = await onayIste({
      baslik: 'Kullanıcı silinsin mi?',
      mesaj:
        `${kullanici.email} kalıcı olarak silinecek ve geri getirilemez. ` +
        'Yaptığı işlemlerin kaydı korunur (denetim için). ' +
        'Çoğu durumda hesabı kapatmak yeterlidir — silmek gerekmez.',
      onayMetni: 'Kalıcı olarak sil',
      tehlikeli: true,
    });
    if (!onay) return;
    setIslemdekiId(kullanici.id);
    sil.mutate(kullanici.id);
  };

  const yukleniyor = kullanicilar.isPending || roller.isPending;
  const hata = kullanicilar.error ?? roller.error;
  const duzenlenebilir = kullanicilar.data?.canEdit ?? false;

  return (
    <Kabuk
      baslik="Kullanıcılar"
      altBaslik={
        kullanicilar.data ? `${kullanicilar.data.items.length} panel kullanıcısı` : 'Panele kimler girebilir'
      }
      eylemler={
        duzenlenebilir ? (
          <Dugme
            gorunum={formAcik ? 'ikincil' : 'birincil'}
            ikon={<UserPlus size={15} />}
            onClick={() => setFormAcik((a) => !a)}
          >
            {formAcik ? 'Vazgeç' : 'Kullanıcı ekle'}
          </Dugme>
        ) : undefined
      }
    >
      {hata != null && (
        <HataKutusu
          hata={hata}
          tekrarDene={() => {
            void kullanicilar.refetch();
            void roller.refetch();
          }}
        />
      )}

      {formAcik && roller.data && (
        <Kart>
          <KartBasligi
            baslik="Yeni kullanıcı"
            aciklama="Şifre otomatik üretilir ve yalnızca bir kez gösterilir"
          />
          <KartGovdesi>
            <EklemeFormu
              roller={roller.data.items}
              bekliyor={ekle.isPending}
              onEkle={(govde) => ekle.mutate(govde)}
            />
          </KartGovdesi>
        </Kart>
      )}

      <Kart>
        <KartBasligi baslik="Panel kullanıcıları" />
        {yukleniyor ? (
          <Yukleniyor satir={4} />
        ) : !kullanicilar.data || kullanicilar.data.items.length === 0 ? (
          <BosDurum
            ikon={<ShieldCheck size={32} strokeWidth={1.5} />}
            baslik="Henüz kullanıcı yok"
            aciklama="Panele girebilecek kişileri buradan eklersiniz."
          />
        ) : (
          <KartGovdesi>
            <ul className="flex flex-col gap-3">
              {kullanicilar.data.items.map((kullanici) => (
                <KullaniciSatiri
                  key={kullanici.id}
                  kullanici={kullanici}
                  roller={roller.data?.items ?? []}
                  duzenlenebilir={duzenlenebilir}
                  silinebilir={kullanicilar.data.canDelete}
                  islemde={islemdekiId === kullanici.id}
                  onRolDegistir={(rolId) => void rolDegistir(kullanici, rolId)}
                  onDurumDegistir={(aktif) => void durumDegistir(kullanici, aktif)}
                  onSifreSifirla={() => void sifreSifirla(kullanici)}
                  onSil={() => void silmeyiSor(kullanici)}
                />
              ))}
            </ul>
          </KartGovdesi>
        )}
      </Kart>
    </Kabuk>
  );
}
